import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Continue to wherever the user came from (e.g. /report, /pricing);
  // first-time visitors with no context land on the safety screen.
  const next = location.state?.from || "/safety-screen";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      navigate(next, { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto py-16 animate-fade-in">
      <Card className="p-8 bg-white border-forest/10">
        <div className="flex items-center gap-2 text-forest mb-2">
          <UserPlus className="w-4 h-4" /> <span className="text-xs uppercase tracking-[0.2em] font-medium">Create account</span>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Join Apexture</h1>
        <p className="text-sm text-muted-foreground mb-6">Free to sign up. Full access is ₹199 for 3 months (intro price).</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Your name</label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Email</label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Password (min 6 chars)</label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-forest hover:bg-forest/90 text-bone" data-testid="signup-submit">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-center mt-6 text-muted-foreground">
          Already have an account? <Link to="/login" state={{ from: next }} className="text-forest font-medium hover:underline">Sign in</Link>
        </p>
        <p className="text-[11px] text-center mt-4 text-muted-foreground">
          By signing up, you agree to our <Link to="/terms" className="underline">Terms</Link>, <Link to="/privacy" className="underline">Privacy Policy</Link>, and <Link to="/disclaimer" className="underline">Medical Disclaimer</Link>.
        </p>
      </Card>
    </div>
  );
}
